import Pagination from 'rc-pagination';
import _ from 'lodash';
import 'rc-pagination/assets/index.css';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Row, Col, Button, Input } from 'reactstrap';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment';
import { format, startOfDay } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { getAllLogs } from '../../actions/BoxSessionLogging';
import { getSystemLogs } from '../../actions/SystemLogs';
import SetLimitEntries from '../../components/SetLimitEntries';
import Breadcrumbs from '../../components/Breadcrumb';
import ViewMore from '../../components/ViewMore';
import Calendar from '../../Res/Icon/Calendar.png';

import './systemLog.css';
import { ENG_LOCALE } from '../../lib/constants';
import NavLogBar from './Components/NavLogBar';
import SystemLogTable from './Components/SystemLogTable';
import OobLogTable from './Components/OobLogTable';
import utils from '../../lib/utils';
import loadingSpinner from '../../Res/logo-loading.gif';

const sort = { "createdAt": -1 };

const ranges = {
  Today: [moment(), moment()],
  Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
  'Last 7 Days': [moment().subtract(6, 'days'), moment()],
  'Last 30 Days': [moment().subtract(29, 'days'), moment()],
  'This Month': [moment().startOf('month'), moment().endOf('month')],
  'Last Month': [
    moment()
      .subtract(1, 'month')
      .startOf('month'),
    moment()
      .subtract(1, 'month')
      .endOf('month'),
  ],
};

const SystemLog = () => {
  const token = localStorage.getItem('jwtToken') || null;
  const dispatch = useDispatch();

  const data = useSelector(state => state.AppState.boxDetails.systemLogs) || {};
  const user = useSelector(state => state.AppState.user);
  const [loading, setLoading] = useState(false);
  const [tableDocs, setTableDocs] = useState([]);
  const latestReqRef = useRef(0);

  const [dateFrom, setDateFrom] = useState(moment().subtract(6, 'days')._d);
  const [dateTo, setDateTo] = useState(startOfDay(new Date()));
  const dateChange = (e, picker) => {
    setDateFrom(picker.startDate.toDate());
    setDateTo(picker.endDate.toDate());
  };

  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState(() => (user?.role === 'admin' ? 'oob' : 'device'));
  useEffect(() => {
    if (!loading) {
      setTableDocs(data?.docs || []);
    }
  }, [data?.docs, loading]);

  useEffect(() => {
    if (!!keyword?.trim().length || (dateFrom && dateTo)) {
      debouncedDispatchQuery(1);
    }
    return debouncedDispatchQuery.cancel;
  }, [keyword, dateFrom, dateTo]);

  // Handle user role changes after component mount
  useEffect(() => {
    if (user?.role === 'admin' && selected !== 'oob') {
      setSelected('oob');
    }
  }, [user?.role]);

  const dispatchQuery = _.debounce((pageVal, limitVal) => {
    const { page, limit } = data;
    const pagination = { page: pageVal ? pageVal : page, limit: limitVal ? limitVal : limit, sort };
    const filters = { from: dateFrom, to: dateTo, keyword };
    if (selected === 'device') {
      const reqId = ++latestReqRef.current;
      setLoading(true);
      setTableDocs([]);
      Promise.resolve(dispatch(getAllLogs(token, pagination, filters)))
        .finally(() => {
          if (latestReqRef.current === reqId) {
            setLoading(false);
          }
        });
    } else {
      dispatch(getSystemLogs(token, pagination, filters));
    }
  }, 300);
  const debouncedDispatchQuery = useCallback(dispatchQuery, [dateFrom, dateTo, keyword, selected]);

  const handleSearch = e => {
    setKeyword(e.target.value);
  };
  const onTabChange = useCallback(async e => {
    setSelected(e);
    setDateFrom(moment().subtract(6, 'days')._d);
    setDateTo(startOfDay(new Date()));
    setKeyword('');
  }, []);

  return (
    <>
      <div className="filter-section">
        <Row>
          {/* <Col md={6} sm={12}>
            <Breadcrumbs />
          </Col> */}
          {['device', 'system'].includes(selected) ? (
            <Col md={12} sm={12} className="d-flex justify-content-end">
              <Row className="justify-content-end row">
                <Col style={{ marginRight: '.5rem', display: 'flex' }} md="2.0">
                  <Input
                    name="keyword-search"
                    placeholder='keyword'
                    value={keyword}
                    onChange={e => handleSearch(e)}
                  />
                </Col>
                {dateFrom && dateTo && (
                  <Col md={3.5} style={{ marginRight: '1rem' }}>
                    <DateRangePicker
                      autoUpdateInput
                      startDate={dateFrom}
                      endDate={dateTo}
                      onApply={dateChange}
                      showCustomRangeLabel
                      alwaysShowCalendars
                      ranges={ranges}
                      opens="left"
                    >
                      <div className="input-group" style={{ alignItems: 'center' }}>
                        <span className="input-group-btn">
                          <Button
                            className="default date-range-toggle"
                            style={{
                              background: '#DDE6FA',
                              border: 'none',
                              color: '#FF2437',
                              width: '4rem',
                            }}
                          >
                            <img style={{ height: '22px' }} src={Calendar} alt="..."></img>
                          </Button>
                        </span>
                        <div className="view-data-input">
                          {format(dateFrom, 'MM/dd/yyyy')} - {format(dateTo, 'MM/dd/yyyy')}
                        </div>
                      </div>
                    </DateRangePicker>
                  </Col>
                )}
              </Row>
            </Col>
          ) : null}
        </Row>
      </div>

      <div className="viewContainer">
        {user && <NavLogBar selected={selected} onTabChange={onTabChange} user={user} />}
        {selected === 'device' ? (
          <>
            <div style={{ position: 'relative' }}>
              {loading ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.7)',
                    zIndex: 999,
                  }}
                >
                  <img src={loadingSpinner} alt="loading" style={{ height: '200px' }} />
                </div>
              ) : null}

            <div className="table-header-fixed">
              <table>
                <thead>
                  <tr>
                    <th className="cursor-pointer">Device Name</th>
                    <th className="cursor-pointer">Serial number</th>
                    <th className="cursor-pointer">Users</th>
                    <th className="cursor-pointer" style={{ width: '300px' }}>
                      Actions
                    </th>
                    <th className="cursor-pointer">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {tableDocs?.map(element => (
                      <tr key={element?._id || element?.createdAt}>
                        <td>{element?.box?.displayName || '-'}</td>
                        <td>{element?.box?.serialNumber || '-'}</td>
                        <td>{element?.user?.name || '-'}</td>
                        <td className="action-description">
                          <ViewMore element={element} />
                        </td>
                        <td>
                          {utils.getDateTimeFormat(element?.createdAt, 'both')}
                        </td>
                      </tr>
                     ))}
                </tbody>
              </table>
                       </div>
            </div>
            {data && Object.keys(data).length ? (
              <Row className="justify-content-md-center">
                <SetLimitEntries
                  value={10}
                  disabled={loading}
                  onChange={limit => {
                    dispatchQuery(false, limit);
                  }}
                />

                <Pagination
                  locale={ENG_LOCALE}
                  className="pagination-container"
                  disabled={loading}
                  onChange={page => {
                    dispatchQuery(page, false);
                  }}
                  current={data.page}
                  total={data.totalDocs}
                  pageSize={data.limit}
                  showLessItems
                  showTitle={false}
                  showTotal={(total, range) => `${range[0]} - ${range[1]} of ${total} items`}
                />
              </Row>
            ) : null}
          </>
        ) : selected === 'system'
          ? <SystemLogTable filters={{ from: dateFrom, to: dateTo, keyword }}/>
            : selected === 'oob' && (<OobLogTable key="oob-log-table" />)
        }
      </div>
    </>
  );
};

export default SystemLog;
